import React, { useState, useEffect } from 'react';
import {
  Box,
  Input,
  Button,
  Text,
  VStack,
  Link,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  InputGroup,
  InputRightElement,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, SettingsIcon } from '@chakra-ui/icons';
import axios from 'axios';
import { getAuthToken } from 'helpers/helpers';
import { API_URL } from 'constants';
import ForgotPassword from './forgotPassword';

export default function MasterPass() {
  const [isMasterPasswordSet, setIsMasterPasswordSet] = useState(null);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isForgotPasswordOpen, onOpen: onForgotPasswordOpen, onClose: onForgotPasswordClose } = useDisclosure();
  const [redirectTimeout, setRedirectTimeout] = useState(null);
  const [modalMessage, setModalMessage] = useState('');
  const [countdown, setCountdown] = useState(5);
  const [masterPasswordForWipe, setMasterPasswordForWipe] = useState('');
  const { isOpen: isWipeDialogOpen, onOpen: onWipeDialogOpen, onClose: onWipeDialogClose } = useDisclosure();

  useEffect(() => {
    const fetchMasterPassStatus = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API_URL}/api/user/masterpass/status`, {
          headers: {
            'x-access-token': token,
            Authorization: `Bearer ${token}`,
          },
        });

        setIsMasterPasswordSet(response.data.isUsingMasterPass);
      } catch (error) {
        console.error('Error fetching master password status:', error);
        setError('Erreur lors de la récupération du statut du mot de passe master.');
        setIsMasterPasswordSet(null);
      }
    };

    fetchMasterPassStatus();
  }, []);

  useEffect(() => {
    if (isOpen) {
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setModalMessage('');
  
    if (!newPassword || !confirmNewPassword) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
  
    if (newPassword !== confirmNewPassword) {
      setError('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }
  
    const token = getAuthToken();
  
    try {
      if (isMasterPasswordSet) {
        if (!oldPassword) {
          setError('Veuillez saisir votre ancien mot de passe master.');
          return;
        }
  
        await axios.put(
          `${API_URL}/api/auth/update-master-password`,
          {
            oldMasterPassword: oldPassword,
            newMasterPassword: newPassword,
          },
          {
            headers: {
              'x-access-token': token,
              Authorization: `Bearer ${token}`,
            },
          }
        );
  
        setSuccess('Mot de passe master mis à jour avec succès.');
        setModalMessage(
          'Votre mot de passe master a été mis à jour avec succès. Vous allez être redirigé vers la page de connexion pour vous reconnecter avec votre nouveau mot de passe.'
        );
      } else {
        await axios.post(
          `${API_URL}/api/auth/set-master-password`,
          {
            masterPassword: newPassword,
          },
          {
            headers: {
              'x-access-token': token,
              Authorization: `Bearer ${token}`,
            },
          }
        );
  
        setSuccess('Mot de passe master défini avec succès.');
        setModalMessage(
          'Votre mot de passe master a été défini avec succès. Vous allez être redirigé vers la page de connexion pour vous reconnecter avec votre nouveau mot de passe.'
        );
      }
  
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
  
      setIsMasterPasswordSet(true);
      onOpen();

      const timeout = setTimeout(() => {
        window.location.href = '/auth/sign-in/default';
      }, 5000);
      setRedirectTimeout(timeout);
    } catch (err) {
      console.error('Error:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Une erreur est survenue.');
      }
    }
  };
  
  const handleWipeMasterPassword = async () => {
    setError('');
    setSuccess('');
    setModalMessage('');
  
    if (!masterPasswordForWipe) {
      setError('Veuillez saisir votre mot de passe master.');
      return;
    }
  
    const token = getAuthToken();
  
    try {
      await axios.put(
        `${API_URL}/api/auth/wipe-master-password`,
        { masterPassword: masterPasswordForWipe },
        {
          headers: {
            'x-access-token': token,
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      setSuccess('Mot de passe master supprimé avec succès.');
      setIsMasterPasswordSet(false);
      setModalMessage(
        'Votre mot de passe master a été supprimé avec succès. Vous allez être redirigé vers la page de connexion pour vous reconnecter avec votre mot de passe régulier.'
      );
      onClose();
      onOpen();

      const timeout = setTimeout(() => {
        window.location.href = '/auth/sign-in/default';
      }, 5000);
      setRedirectTimeout(timeout);
    } catch (err) {
      console.error('Error wiping master password:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Une erreur est survenue lors de la suppression du mot de passe master.');
      }
    }
  };

  const handleForgotPassword = () => {
    onForgotPasswordOpen();
  };

  const handleRedirectToLogin = () => {
    if (redirectTimeout) {
      clearTimeout(redirectTimeout);
    }
    onClose();
    window.location.href = '/auth/sign-in/default';
  };

  return (
    <Box mt="64px" px={4}>
      <VStack spacing={4} align="stretch">
        {isMasterPasswordSet === null && (
          <Box textAlign="center">
            {error ? (
              <Alert status="error">
                <AlertIcon />
                <AlertTitle>Erreur</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <Spinner size="xl" color="teal.500" />
            )}
          </Box>
        )}

        {isMasterPasswordSet !== null && (
          <>
            {isMasterPasswordSet === false && (
              <Alert status="warning">
                <AlertIcon />
                Vous n'utilisez pas de mot de passe master. Veuillez en définir un pour sécuriser votre tableau de bord.
              </Alert>
            )}

            {isMasterPasswordSet && (
              <Alert status="success">
                <AlertIcon />
                Vous utilisez actuellement le mot de passe master.
              </Alert>
            )}

            <Text fontSize="lg" fontWeight="bold" color="teal.500">
              {isMasterPasswordSet
                ? 'Modifier le mot de passe master'
                : 'Définir le mot de passe master'}
            </Text>

            {isMasterPasswordSet && (
              <InputGroup>
                <Input
                  type={showOldPassword ? 'text' : 'password'}
                  placeholder="Ancien mot de passe master"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                />
                <InputRightElement>
                  <IconButton
                    icon={showOldPassword ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    variant="ghost"
                    aria-label="Toggle password visibility"
                  />
                </InputRightElement>
              </InputGroup>
            )}

            <InputGroup>
              <Input
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Nouveau mot de passe master"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <InputRightElement>
                <IconButton
                  icon={showNewPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  variant="ghost"
                  aria-label="Toggle password visibility"
                />
              </InputRightElement>
            </InputGroup>

            <InputGroup>
              <Input
                type={showConfirmNewPassword ? 'text' : 'password'}
                placeholder="Confirmez le nouveau mot de passe master"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
              <InputRightElement>
                <IconButton
                  icon={showConfirmNewPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                  variant="ghost"
                  aria-label="Toggle password visibility"
                />
              </InputRightElement>
            </InputGroup>

            {error && (
              <Text fontSize="sm" color="red.500">
                {error}
              </Text>
            )}

            {success && (
              <Text fontSize="sm" color="green.500">
                {success}
              </Text>
            )}

            <Box display="flex" width="100%">
              <Button
                colorScheme="teal"
                flex="1"
                onClick={handleSubmit}
              >
                {isMasterPasswordSet ? 'Modifier le mot de passe' : 'Définir le mot de passe'}
              </Button>
              {isMasterPasswordSet && (
                <Menu>
                  <MenuButton
                    as={Button}
                    colorScheme="blue"
                    size="md"
                    ml={2}
                    border="2px solid"
                    borderColor="blue.500"
                    leftIcon={<SettingsIcon />}
                  >
                    Options
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={onWipeDialogOpen}>
                      Supprimer le mot de passe master
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
            </Box>

            {isMasterPasswordSet && (
              <Link
                color="teal.500"
                fontSize="sm"
                onClick={handleForgotPassword}
                textDecoration="underline"
                alignSelf="flex-start"
              >
                J'ai oublié mon mot de passe master
              </Link>
            )}
          </>
        )}
        <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} closeOnEsc={false}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Opération réussie</ModalHeader>
            <ModalBody>
              {modalMessage}<br />
              Cliquez sur "OK" ou attendez {countdown} seconde{countdown > 1 ? 's' : ''} pour être redirigé.
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="teal" onClick={handleRedirectToLogin}>
                OK
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal isOpen={isForgotPasswordOpen} onClose={onForgotPasswordClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalBody>
              <ForgotPassword resetMasterPwdOnly={true} centered={false} />
            </ModalBody>
            <ModalFooter>
              <Button onClick={onForgotPasswordClose} color="red.500">
                Fermer
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal isOpen={isWipeDialogOpen} onClose={onWipeDialogClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Confirmer la suppression</ModalHeader>
            <ModalBody>
              <Text mb={4}>
                Veuillez saisir votre mot de passe master pour confirmer la suppression.
              </Text>
              <InputGroup>
                <Input
                  type="password"
                  placeholder="Mot de passe master"
                  value={masterPasswordForWipe}
                  onChange={(e) => setMasterPasswordForWipe(e.target.value)}
                />
              </InputGroup>
              <Link
                color="teal.500"
                fontSize="sm"
                onClick={handleForgotPassword}
                textDecoration="underline"
                mt={2}
                display="block"
              >
                Mot de passe oublié ?
              </Link>
              {error && (
                <Text fontSize="sm" color="red.500" mt={2}>
                  {error}
                </Text>
              )}
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="red" onClick={handleWipeMasterPassword}>
                Supprimer
              </Button>
              <Button onClick={onWipeDialogClose} ml={3}>
                Annuler
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
}
